/**
 * 🎭 M.U.S.E. API Service
 * Connects NOONX dashboards to the live M.U.S.E. AGI server
 * 
 * Server: https://api.noonx.com/muse
 * Features: AI-powered influencer marketing insights
 */

// M.U.S.E. Server Configuration
const MUSE_API_URL = import.meta.env.VITE_MUSE_API_URL || 'https://api.noonx.com/muse';

export interface MuseResponse {
  response: string;
  confidence: number;
  decision_quality?: string;
  agreement?: number;
  agents_used?: number;
  llm_sources?: string[];
  gemini_helped?: boolean;
  supabase_data_used?: boolean;
  sources?: string[];
  fast_path?: boolean;
  response_type?: string;
  success: boolean;
  error?: string;
  consensus_votes?: Array<{
    agent: string;
    emoji: string;
    confidence: number;
    is_outlier: boolean;
  }>;
  reasoning_steps?: Array<{
    step: number;
    action: string;
    thought: string;
  }>;
}

export interface MuseHealthStatus {
  status: string;
  agents: number;
  agi_core: string;
  gemini_available: boolean;
  llm_orchestrator: boolean;
  muse_memory: boolean;
  self_learning: boolean;
  version: string;
}

export interface MuseWorldModelBelief {
  id: string;
  subject: string;
  predicate: string;
  object: string;
  confidence: number;
  evidence: string;
  domain: string;
  created_at: string;
  updated_at: string;
}

class MuseService {
  private baseUrl: string;
  private userId: string | null = null;
  private sessionId: string;

  constructor() {
    this.baseUrl = MUSE_API_URL;
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Set the current user for personalized responses
   */
  setUser(userId: string) {
    this.userId = userId;
  }

  /**
   * Check if M.U.S.E. server is healthy
   */
  async checkHealth(): Promise<MuseHealthStatus | null> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        console.warn('M.U.S.E. health check failed:', response.status);
        return null;
      }
      
      return await response.json();
    } catch (error) {
      console.warn('M.U.S.E. server unreachable:', error);
      return null;
    }
  }

  /**
   * Send a chat message to M.U.S.E.
   */
  async chat(message: string, context?: Record<string, unknown>): Promise<MuseResponse> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 second timeout

      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          message,
          user_id: this.userId || 'anonymous',
          session_id: this.sessionId,
          context: {
            platform: 'noonx_dashboard',
            ...context,
          },
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`M.U.S.E. API error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          response: "I'm taking longer than expected to think. Please try a simpler question or try again.",
          confidence: 0,
          success: false,
          error: 'timeout',
        };
      }
      
      console.error('M.U.S.E. chat error:', error);
      return {
        response: "I'm having trouble connecting to my AI brain. Let me try to help with basic information instead.",
        confidence: 0,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get influencer pricing information
   */
  async getInfluencerPricing(
    platform: string,
    tier: string,
    category?: string
  ): Promise<MuseResponse> {
    const query = category
      ? `What is the pricing for ${tier} influencers on ${platform} in the ${category} category?`
      : `What is the pricing for ${tier} influencers on ${platform}?`;
    
    return this.chat(query);
  }

  /**
   * Get ROI analysis
   */
  async getROIAnalysis(campaignType?: string): Promise<MuseResponse> {
    const query = campaignType
      ? `What is the typical ROI for ${campaignType} influencer campaigns?`
      : `What is the ROI of influencer marketing?`;
    
    return this.chat(query);
  }

  /**
   * Get campaign recommendations
   */
  async getCampaignRecommendations(
    budget: number,
    goals: string[],
    targetAudience?: string
  ): Promise<MuseResponse> {
    const query = `I have a budget of ₹${budget.toLocaleString('en-IN')} for an influencer campaign. 
    My goals are: ${goals.join(', ')}. 
    ${targetAudience ? `Target audience: ${targetAudience}.` : ''} 
    What influencer strategy do you recommend?`;
    
    return this.chat(query);
  }

  /**
   * Get world model beliefs (AGI knowledge)
   */
  async getWorldModel(): Promise<MuseWorldModelBelief[]> {
    try {
      const response = await fetch(`${this.baseUrl}/world-model`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        console.warn('Failed to get world model:', response.status);
        return [];
      }
      
      const data = await response.json();
      return data.beliefs || [];
    } catch (error) {
      console.warn('World model fetch error:', error);
      return [];
    }
  }

  /**
   * Get available agents info
   */
  async getAgents(): Promise<Record<string, unknown>[]> {
    try {
      const response = await fetch(`${this.baseUrl}/agents`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        return [];
      }
      
      const data = await response.json();
      return data.agents || [];
    } catch (error) {
      console.warn('Agents fetch error:', error);
      return [];
    }
  }

  /**
   * Format M.U.S.E. response for display
   */
  formatResponse(museResponse: MuseResponse): {
    content: string;
    metadata: {
      confidence: number;
      sources: string[];
      agentsUsed: number;
      isFastPath: boolean;
    };
  } {
    return {
      content: museResponse.response,
      metadata: {
        confidence: museResponse.confidence || 0,
        sources: museResponse.sources || [],
        agentsUsed: museResponse.agents_used || 0,
        isFastPath: museResponse.fast_path || false,
      },
    };
  }
}

// Export singleton instance
export const museService = new MuseService();

// Export types
export type { MuseService };
